/**
 * The body of a catalogue info popover — `.m-popover-head` / `.m-popover-rows`
 * in the prototype's modals.css, which `MarketInfoPopover` and
 * `OutcomeInfoPopover` both fill.
 *
 * Its own row language rather than `DefinitionList`. The two look alike from a
 * distance and are not the same thing: the definition list is a *card's*
 * metadata block — 14px monospace throughout, a hairline between every row, and
 * no horizontal padding of its own because the card supplies it. The popover's
 * rows are 12.5px, ruleless, padded to the panel edge, and monospace only where
 * the value is an identifier. Handing a bare `DefinitionList` to `Popover` —
 * which is padding-free on purpose, so the reel more-menu can fill it edge to
 * edge — is what left these panels' text flush against the border.
 *
 * The heading is the prototype's, and it earns more here than in a card: the
 * panel is portalled to `document.body` and can flip above its anchor, so
 * "which row is this about" is a question it has to answer by itself.
 */
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';

export interface InfoPanelRow {
  key: string;
  value: ReactNode;
  /** `.m-popover-mono` — an identifier or a figure, not prose. */
  mono?: boolean;
  /**
   * `.m-popover-truncate` — ellipsised at 160px, with the whole value on the
   * element's `title`.
   *
   * The prototype puts this on its `ref` rows alone, because its own ids are
   * short enough to sit inline (`MK-219488EA`). Every id Caesars gives us is a
   * 36-character UUID, so every mono row here is the case the class was drawn
   * for — left to themselves they break mid-string against the panel edge.
   */
  truncate?: boolean;
}

export interface InfoPanelProps {
  /** `.m-popover-eyebrow` — the kind of thing, e.g. "Market". */
  eyebrow: string;
  /** `.m-popover-title` — the thing's own name. */
  title: string;
  rows: InfoPanelRow[];
}

export const InfoPanel = ({ eyebrow, title, rows }: InfoPanelProps) => (
  <>
    {/* `.m-popover-head` — a banded header, not just the first thing in the box. */}
    <Box
      sx={(theme: Theme) => ({
        padding: '12px 14px 10px',
        borderBottom: `${theme.tokens.border.width} solid ${theme.tokens.color.line}`,
        backgroundColor: theme.tokens.color.surfaceMuted,
      })}
    >
      <Box
        component="span"
        sx={(theme: Theme) => ({
          display: 'block',
          fontSize: '10.5px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: theme.tokens.color.ink4,
        })}
      >
        {eyebrow}
      </Box>
      <Box
        component="h3"
        sx={(theme: Theme) => ({
          margin: '3px 0 0',
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '-0.005em',
          color: theme.tokens.color.ink,
        })}
      >
        {title}
      </Box>
    </Box>

    {/* `.m-popover-rows` — ruleless; the head's border is the only line. */}
    <Box
      component="dl"
      sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '4px 0' }}
    >
      {rows.map((row) => (
        <Box
          key={row.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '7px 14px',
            fontSize: '12.5px',
          }}
        >
          <Box
            component="dt"
            sx={(theme: Theme) => ({ color: theme.tokens.color.ink3, flexShrink: 0 })}
          >
            {row.key}
          </Box>
          <Box
            component="dd"
            title={row.truncate && typeof row.value === 'string' ? row.value : undefined}
            sx={(theme: Theme) => ({
              margin: 0,
              fontWeight: 500,
              textAlign: 'right',
              minWidth: 0,
              color: theme.tokens.color.ink,
              ...(row.mono
                ? {
                    fontFamily: theme.tokens.font.mono,
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                  }
                : {}),
              ...(row.truncate
                ? {
                    display: 'inline-block',
                    maxWidth: '160px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }
                : {}),
            })}
          >
            {row.value}
          </Box>
        </Box>
      ))}
    </Box>
  </>
);
